import { z } from 'zod';
import { itemEnvelope, zId, zInstant } from './envelope';
import { apiRequest, apiVoid } from './http';

/** BillerAPI `TenantResult`. */
export const tenantSchema = z.object({
  id: zId,
  slug: z.string(),
  name: z.string(),
  status: z.string(),
  default_currency: z.string(),
  time_zone: z.string(),
  country: z.string(),
  /** A demo tenant: its data is made up, so it sends no email and connects no accounting system. */
  demo: z.boolean().default(false),
  created_at: zInstant.nullable(),
});
export type Tenant = z.infer<typeof tenantSchema>;

/** BillerAPI `MembershipSummary`. */
export const membershipSchema = z.object({
  tenant_id: zId,
  tenant_name: z.string(),
  tenant_slug: z.string(),
  role: z.string(),
});
export type Membership = z.infer<typeof membershipSchema>;

export const environmentSchema = z.enum(['test', 'live']);

/** BillerAPI `SessionResult`. `token` is present only right after login/switch. */
export const sessionSchema = z.object({
  token: z.string().nullable(),
  expires_at: zInstant.nullable(),
  user: z.object({ id: zId, email: z.string(), name: z.string() }),
  tenant: tenantSchema,
  environment: environmentSchema,
  role: z.string().nullable(),
  abilities: z.array(z.string()),
  memberships: z.array(membershipSchema),
});
export type Session = z.infer<typeof sessionSchema>;

const sessionEnvelope = itemEnvelope(sessionSchema);

export interface LoginInput {
  email: string;
  password: string;
  tenant_id?: string | undefined;
  environment?: 'test' | 'live' | undefined;
}

export const sessionApi = {
  /** POST /v1/auth/sessions → a new session token (shown once). */
  login: async (input: LoginInput): Promise<Session> =>
    (await apiRequest(sessionEnvelope, '/auth/sessions', { method: 'POST', body: input, auth: false })).data,

  /** GET /v1/auth/session → the current credential's context (token null). */
  describe: async (signal?: AbortSignal): Promise<Session> =>
    (await apiRequest(sessionEnvelope, '/auth/session', { signal })).data,

  /** POST /v1/auth/session/switch → a NEW token for the target; the old one is revoked. */
  switch: async (input: { tenant_id?: string | undefined; environment: 'test' | 'live' }): Promise<Session> =>
    (await apiRequest(sessionEnvelope, '/auth/session/switch', { method: 'POST', body: input })).data,

  /** DELETE /v1/auth/session → revokes the token (204). */
  logout: (): Promise<void> => apiVoid('/auth/session', { method: 'DELETE' }),
};
